/**
 *
 *  Stripe Provider
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 03/04/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useContext } from "react";
import PropTypes from "prop-types";

// Utils
import { loadScript } from "../../utils";
import { ThemeContext } from "../ThemeProvider/ThemeProvider";
import { StateContext } from "../../state";

const STRIPE_URL = "https://js.stripe.com/v3/";
const STRIPE_ID = "stripe-js";

const LOADING_STATE_NONE = "NONE";
const LOADING_STATE_BEGIN = "BEGIN";
const LOADING_STATE_LOADED = "LOADED";

export const contextTypes = {
  addStripeLoadListener: PropTypes.func,
};

export const StripeContext = React.createContext(null);

export class StripeProvider extends React.Component {
  static propTypes = {
    apiKey: PropTypes.string,
    stripe: PropTypes.object,
    children: PropTypes.node,
  };

  static defaultProps = {
    children: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      loadingState: LOADING_STATE_NONE,
      stripe: null,
    };

    this.isUnmounted = false;

    this.handleLoaded = this.handleLoaded.bind(this);
  }

  handleLoaded() {
    if (this.isUnmounted) {
      return;
    }

    const stripe = window.Stripe(process.env.REACT_APP_STRIPE_API_KEY);

    this.setState({
      stripe,
      loadingState: LOADING_STATE_LOADED,
    });

    this.props.onLoaded && this.props.onLoaded(stripe);
  }

  UNSAFE_componentWillMount() {
    this._meta = {
      stripe: this.props.stripe,
    };
    this._didNotifyListeners = false;
    this._listeners = [];
  }

  componentDidMount() {
    const { loadingState } = this.state;

    if (loadingState !== LOADING_STATE_NONE /* || !canUseDOM*/) {
      return;
    }

    this.setState({
      loadingState: LOADING_STATE_BEGIN,
    });

    loadScript(STRIPE_URL, STRIPE_ID, this.handleLoaded);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (!this._didNotifyListeners && this.state.stripe) {
      // Notify the listeners if we've finally been given the Stripe object
      this._didNotifyListeners = true;
      this._meta.stripe = this.state.stripe;
      this._listeners.forEach((cb) => {
        cb(this.state.stripe);
      });
    }
  }

  render() {
    return (
      <StripeContext.Provider
        value={{
          addStripeLoadListener: (cb) => {
            if (this._meta.stripe) {
              cb(this._meta.stripe);
            } else {
              this._listeners.push(cb);
            }
          },
          stripe: this.state.stripe,
        }}
      >
        {React.Children.only(this.props.children)}
      </StripeContext.Provider>
    );
  }
}

export const useStripe = () => {
  const context = React.useContext(StripeContext);

  if (!context) {
    throw new Error("useStripe must be used within the StripeProvider");
  }

  return context;
};

export default StripeProvider;
